import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/api';
import usePopTransition from '../common/usePopTransition';
import PeoplePicker from '../common/PeoplePicker';
import { AS_REAL_ACCOUNT } from '../../utils/assumeUser';

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

/** What a person is called in the list: their name where the login has one, else the login. */
export const nameOf = (u) => String(u.real_name || '').trim() || String(u.username || '').trim();

/**
 * Only the logins that have a Company Directory profile: one with none (a login on the Break Scheduler roster the directory does not hold) has
 * nothing to show as, and "No Employee Dossier for this account" is all the Admin would get. Where no login has one at all (a copy with no
 * directory loaded) everyone is kept, so the control still works there.
 */
export function onlyWithProfile(users) {
  const list = users || [];
  return list.some((u) => u.has_profile) ? list.filter((u) => u.has_profile) : list;
}

/** The people to offer: everyone but the Admin themself, filtered by what was typed (name, login, email, team, position). */
export function filterPeople(users, query, labelOf, selfId) {
  const q = norm(query);
  return (users || [])
    .filter((u) => Number(u.id) !== Number(selfId))
    .filter((u) => !q || [nameOf(u), u.username, u.email, u.team, labelOf(u.role)].some((v) => norm(v).includes(q)))
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' }));
}

/**
 * The Admin's "view the app as a user" control (the person icon in the navbar): pick someone and the whole app, server
 * included, answers as them, read-only. Replaces the old "view as a position" menu, which only hid things on screen
 * while the server still answered as the Admin, so it could not show what a person's scopes really give them.
 *
 * The panel holds one field, and the field is the search (common/PeoplePicker, `field`): typing in it filters the people dropping down under it, each with
 * their position and team, the arrow keys move and Enter picks. The field takes the cursor and opens its list as soon as the panel opens.
 */
export default function AssumeUserMenu({ assumed, realUserId, onAssume }) {
  const [open, setOpen] = useState(false);
  const pop = usePopTransition(open);
  const rootRef = useRef(null);
  const [people, setPeople] = useState(null);
  const [roles, setRoles] = useState({});
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      // The list of people is drawn on <body> (so nothing clips it), outside this panel, but is part of it.
      if (event.target.closest && event.target.closest('.people-picker')) return;
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Loaded when the menu is first opened, as the real account (an Admin may already be viewing as somebody).
  useEffect(() => {
    if (!open || people) return undefined;
    let cancelled = false;
    Promise.all([
      api.get('/api/users', { headers: AS_REAL_ACCOUNT, params: { include: 'profile' } }),
      api.get('/api/role-permissions', { headers: AS_REAL_ACCOUNT }).catch(() => ({ data: { roles: [] } }))
    ])
      .then(([u, r]) => {
        if (cancelled) return;
        setPeople(Array.isArray(u.data) ? onlyWithProfile(u.data) : []);
        setRoles(Object.fromEntries(((r.data && r.data.roles) || []).map((role) => [role.slug, role.label])));
      })
      .catch(() => { if (!cancelled) setError('Could not load the people.'); });
    return () => { cancelled = true; };
  }, [open, people]);

  const labelOf = useCallback((slug) => roles[slug] || slug || '', [roles]);
  // Everyone but the Admin themself; what is typed is matched by the picker through filterPeople (name, login, email, team, position).
  const offered = useMemo(() => (people || []).filter((u) => Number(u.id) !== Number(realUserId)), [people, realUserId]);
  const searchFn = useCallback((list, query) => filterPeople(list, query, labelOf, realUserId), [labelOf, realUserId]);
  const subOf = useCallback((p) => [labelOf(p.role), p.team, assumed && assumed.id === p.id ? 'Viewing now' : ''].filter(Boolean).join(' · '), [labelOf, assumed]);

  // The cursor goes into the field when the panel opens, which opens its list.
  useEffect(() => {
    if (!open) return;
    const input = rootRef.current && rootRef.current.querySelector('.people-picker__field-input');
    if (input) input.focus();
  }, [open, pop.mounted]);

  const choose = async (person) => {
    if (busyId != null) return;
    setBusyId(person.id);
    setError('');
    const result = await onAssume(person.id);
    // On success the app restarts as that person; only a failure comes back here.
    if (!result || !result.success) {
      setBusyId(null);
      setError((result && result.error) || 'Could not start the view.');
    }
  };

  return (
    <div className="suite-role-preview" ref={rootRef}>
      <button
        type="button"
        className={`suite-icon-btn suite-role-preview-btn ${assumed ? 'is-previewing' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={assumed ? `Viewing as ${assumed.name}` : 'View the app as another user'}
      >
        <svg className="suite-role-preview-icon" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="5.1" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M2.7 14c0-2.8 2.4-4.5 5.3-4.5s5.3 1.7 5.3 4.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
      {pop.mounted && (
        <div className={`suite-role-preview-panel suite-assume-panel ui-pop${pop.closing ? ' is-closing' : ''}`} role="dialog" aria-label="View as">
          <p className="suite-role-preview-head">View as</p>
          {error ? <p className="suite-role-preview-empty" role="alert">{error}</p> : null}
          <PeoplePicker
            field
            people={offered}
            onPick={choose}
            label="Find a person"
            searchLabel="Find a person to view the app as"
            emptyText={error && !people ? 'Could not load the people.' : (people ? 'There is nobody to view as.' : 'Loading people...')}
            searchFn={searchFn}
            labelOf={nameOf}
            subOf={subOf}
            hint="Enter views the app as the highlighted person. Escape closes."
          />
          <p className="suite-role-preview-note">
            You see exactly what they see, and the app is read-only while you do: nothing can be changed in their name.
            Starting a view is recorded in the audit log.
          </p>
        </div>
      )}
    </div>
  );
}
